/**
 * Field Configuration
 * Centralizirane definicije polja za mapiranje
 */

import { FieldDefinition } from '../types';

// ============================================================================
// CORE FIELDS - Osnovna polja, uvijek vidljiva
// ============================================================================

export const CORE_FIELDS: FieldDefinition[] = [
  { key: 'sku', label: 'SKU', required: true },
  { key: 'name', label: 'Naziv proizvoda' },
  { key: 'regular_price', label: 'Cijena (prodajna)' },
  { key: 'nabavna_cijena', label: 'Nabavna cijena' },
  { key: 'stock_quantity', label: 'Količina' },
  { key: 'description', label: 'Opis' },
  { key: 'ean', label: 'EAN / Barkod' },
  { key: 'brand', label: 'Brend' },
  { key: 'dobavljac', label: 'Dobavljač' },
  { key: 'pdv', label: 'PDV' },
  { key: 'garancija', label: 'Garancija' },
  { key: 'images', label: 'Slike (URL)' },
  { key: 'categories', label: 'Kategorija' },
];

// ============================================================================
// EXTRA FIELDS - Dodatna polja koja korisnik može dodati
// ============================================================================

export const EXTRA_FIELDS: FieldDefinition[] = [
  { key: 'short_description', label: 'Kratki opis' },
  { key: 'sale_price', label: 'Akcijska cijena' },
  { key: 'specifikacije', label: 'Specifikacije (JSONB)' },
  { key: 'weight', label: 'Težina' },
  { key: 'length', label: 'Dužina' },
  { key: 'width', label: 'Širina' },
  { key: 'height', label: 'Visina' },
  { key: 'tags', label: 'Tagovi' },
  { key: 'attributes', label: 'Atributi' },
  { key: 'status', label: 'Status' },
  { key: 'slug', label: 'Slug' },
];

// ============================================================================
// SPECIAL FIELD TYPES
// ============================================================================

// Polja koja podržavaju multi-value (array/split)
export const MULTI_VALUE_FIELDS = ['images', 'categories', 'tags', 'attributes'];

// Polja koja podržavaju keyValue mode (atributi kao key-value objekt)
export const KEY_VALUE_FIELDS = ['meta_data._specifikacije', 'attributes', 'specifikacije'];

// Direktne kolone u bazi (ne idu u meta_data)
export const DIRECT_COLUMNS = ['nabavna_cijena', 'pdv', 'garancija', 'dobavljac', 'specifikacije'];

// ============================================================================
// UPDATE FIELDS DEFAULT
// ============================================================================

export const DEFAULT_UPDATE_FIELDS: Record<string, boolean> = {
  regular_price: true,
  nabavna_cijena: true,
  stock_quantity: true,
  name: false,
  description: false,
  short_description: false,
  images: false,
  categories: false,
  brand: false,
  ean: false,
  garancija: true,
  specifikacije: true,
  weight: false,
  length: false,
  width: false,
  height: false,
};

// Lista polja za Update Fields panel
export const UPDATE_FIELDS_LIST = [
  { key: 'regular_price', label: 'Cijena' },
  { key: 'nabavna_cijena', label: 'Nabavna cijena' },
  { key: 'stock_quantity', label: 'Količina' },
  { key: 'name', label: 'Naziv' },
  { key: 'description', label: 'Opis' },
  { key: 'images', label: 'Slike' },
  { key: 'brand', label: 'Brend' },
  { key: 'ean', label: 'EAN' },
  { key: 'garancija', label: 'Garancija' },
  { key: 'specifikacije', label: 'Specifikacije' },
  { key: 'categories', label: 'Kategorije' },
];

// ============================================================================
// AUTO-DETECTION PATTERNS
// ============================================================================

export const AUTO_DETECT_PATTERNS: Record<string, RegExp[]> = {
  'sku': [/sku/i, /code/i, /sifra/i, /wic/i, /productcode/i, /artikal/i],
  'name': [/name/i, /naziv/i, /title/i, /opis/i, /ime/i],
  'regular_price': [/price/i, /cijena/i, /cena/i, /cij/i, /ppc/i],
  'stock_quantity': [/qty/i, /quantity/i, /stock/i, /kolicina/i, /zaliha/i, /lager/i, /zaloga/i],
  'ean': [/ean/i, /barcode/i, /barkod/i, /gtin/i, /upc/i],
  'brand': [/brand/i, /brend/i, /znamka/i, /marka/i, /proizvodjac/i, /manufacturer/i, /blagovnaznamka/i],
  'weight': [/weight/i, /tezina/i, /masa/i],
  'nabavna_cijena': [/nabavna/i, /cost/i, /purchase/i, /buying/i],
};

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Dohvati label za polje po key-u
 */
export function getFieldLabel(key: string): string {
  const coreField = CORE_FIELDS.find(f => f.key === key);
  if (coreField) return coreField.label;

  const extraField = EXTRA_FIELDS.find(f => f.key === key);
  if (extraField) return extraField.label;

  return key;
}

/**
 * Provjeri je li polje multi-value
 */
export function isMultiValueField(key: string): boolean {
  return MULTI_VALUE_FIELDS.includes(key);
}

/**
 * Provjeri je li polje key-value tipa
 */
export function isKeyValueField(key: string): boolean {
  return KEY_VALUE_FIELDS.includes(key);
}

/**
 * Provjeri je li polje direktna kolona
 */
export function isDirectColumn(key: string): boolean {
  return DIRECT_COLUMNS.includes(key);
}
